import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  AUTH_TOKEN,
  AUTH_PARENT_ID,
  AUTH_PARENT_NAME,
  AUTH_TOTAL_CHILDREN,
  AUTH_STUDENTS,
  AUTH_DRIVER_ID,
  AUTH_BUS_ID,
  AUTH_DRIVER_NAME,
  AUTH_EMAIL,
} from "constants/AuthConstant";

import AuthService from "services/AuthService";
import FirebaseService from "services/FirebaseService";

export const initialState = {
  loading: false,
  message: "",
  showMessage: false,
  redirect: "",
  token: localStorage.getItem(AUTH_TOKEN) || null,
  parentId: localStorage.getItem(AUTH_PARENT_ID) || null,
  parentName: localStorage.getItem(AUTH_PARENT_NAME) || null,
  totalChildren: localStorage.getItem(AUTH_TOTAL_CHILDREN) || 0,
  students: JSON.parse(localStorage.getItem(AUTH_STUDENTS)) || [],
  driverid: localStorage.getItem(AUTH_DRIVER_ID) || null,
  driverName: localStorage.getItem(AUTH_DRIVER_NAME) || null,
  email: localStorage.getItem(AUTH_EMAIL) || null,
  busid: JSON.parse(localStorage.getItem(AUTH_BUS_ID)) || null,
};

export const signIn = createAsyncThunk(
  "auth/signIn",
  async (data, { rejectWithValue }) => {
    const { emailaddress, password } = data;
    try {
      const response = await AuthService.login({ emailaddress, password });
      const parent = response?.data;

      if (parent && parent.parentId) {
        const students = parent.students?.map((student) => ({
          id: student.id,
          name: student.name,
          uniqueStudentid: student.uniqueStudentid || null,
          emailAddress: student.emailAddress || null,
          dateofBirth: student.dateofBirth || null,
          mobileNumber: student.mobileNumber || null,
        })) || [];

        localStorage.setItem(AUTH_TOKEN, parent.parentId);
        localStorage.setItem(AUTH_PARENT_ID, parent.parentId);
        localStorage.setItem(AUTH_PARENT_NAME, parent.parentName);
        localStorage.setItem(AUTH_TOTAL_CHILDREN, parent.totalStudents);
        localStorage.setItem(AUTH_STUDENTS, JSON.stringify(students));

        return {
          token: parent.parentId,
          parentId: parent.parentId,
          parentName: parent.parentName,
          totalChildren: parent.totalStudents,
          students,
        };
      } else {
        return rejectWithValue("Invalid credentials. Please try again!");
      }
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const driverSignIn = createAsyncThunk(
  "auth/driverSignIn",
  async (data, { rejectWithValue }) => {
    const { emailaddress, password } = data;
    try {
      const response = await AuthService.driverlogin({ emailaddress, password });
      const driver = response?.data;

      if (driver && driver.id) {
        localStorage.setItem(AUTH_TOKEN, driver.busid);
        localStorage.setItem(AUTH_DRIVER_ID, driver.id);
        localStorage.setItem(AUTH_DRIVER_NAME, driver.name);
        localStorage.setItem(AUTH_BUS_ID, JSON.stringify(driver.busid));
        localStorage.setItem(AUTH_EMAIL, driver.email);

        return {
          token: driver.busid,
          driverid: driver.id,
          driverName: driver.name,
          busid: driver.busid,
          email: driver.email,
        };
      } else {
        return rejectWithValue("Invalid credentials. Please try again!");
      }
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const signOut = createAsyncThunk("auth/signOut", async () => {
  await FirebaseService.signOutRequest();
  localStorage.clear();
});

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false;
      state.redirect = "/";
      state.token = action.payload;
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },
    hideAuthMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
    showLoading: (state) => {
      state.loading = true;
    },
    signOutSuccess: (state) => {
      state.loading = false;
      state.token = null;
      state.parentId = null;
      state.parentName = null;
      state.totalChildren = 0;
      state.students = [];
      state.driverid = null;
      state.driverName = null;
      state.email = null;
      state.busid = null;
      state.redirect = "/";
    },
  },
  extraReducers: (builder) => {
    builder
      // parent signin
      .addCase(signIn.pending, (state) => {
        state.loading = true;
        state.message = "";
        state.showMessage = false;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        localStorage.removeItem(AUTH_DRIVER_ID);
        localStorage.removeItem(AUTH_DRIVER_NAME);
        localStorage.removeItem(AUTH_BUS_ID);
        localStorage.removeItem(AUTH_EMAIL);

        state.loading = false;
        state.redirect = "/";
        state.token = action.payload.token;
        state.parentId = action.payload.parentId;
        state.parentName = action.payload.parentName;
        state.totalChildren = action.payload.totalChildren;
        state.students = action.payload.students;

        state.driverid = null;
        state.driverName = null;
        state.busid = null;
        state.email = null;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      // driver signin
      .addCase(driverSignIn.pending, (state) => {
        state.loading = true;
        state.message = "";
        state.showMessage = false;
      })
      .addCase(driverSignIn.fulfilled, (state, action) => {
        localStorage.removeItem(AUTH_PARENT_ID);
        localStorage.removeItem(AUTH_PARENT_NAME);
        localStorage.removeItem(AUTH_TOTAL_CHILDREN);
        localStorage.removeItem(AUTH_STUDENTS);

        state.loading = false;
        state.redirect = "/";
        state.token = action.payload.token;
        state.driverid = action.payload.driverid;
        state.driverName = action.payload.driverName;
        state.busid = action.payload.busid;
        state.email = action.payload.email;

        state.parentId = null;
        state.parentName = null;
        state.totalChildren = 0;
        state.students = [];
      })
      .addCase(driverSignIn.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signOut.fulfilled, (state) => {
        Object.assign(state, { ...initialState, token: null });
      })
      .addCase(signOut.rejected, (state) => {
        Object.assign(state, { ...initialState, token: null });
      });
  },
});

export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  showLoading,
  signOutSuccess,
} = authSlice.actions;

export const showDriverAuthMessage = authSlice.actions.showAuthMessage;
export const hideDriverAuthMessage = authSlice.actions.hideAuthMessage;
export const showDriverLoading = authSlice.actions.showLoading;

export default authSlice.reducer;